import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { resetPassword } from '../services/api';
import { AuthShell, FOOTLINK, FOOTNOTE, PasswordField, PRIMARY } from './auth/AuthShell';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = searchParams.get('token');

  const long = password.length >= 8;
  const same = password === confirmPassword;
  let hint = 'Eight characters or more.';
  let hintCls = 'text-[#777982]';
  if (password && !long) { hint = 'A little longer: eight characters or more.'; hintCls = 'text-[#8a6d00]'; }
  if (long && confirmPassword && !same) { hint = 'The two do not match yet.'; hintCls = 'text-[#8a6d00]'; }
  if (long && same && confirmPassword) { hint = 'Good. This is what you sign in with from now on.'; hintCls = 'text-[#3f7a2f]'; }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!long || !same) return;
    if (!token) {
      toast.error('This link has no reset token. Ask for a new one.');
      return;
    }
    setIsSubmitting(true);
    try {
      await resetPassword(token, password);
      toast.success('Password updated. Sign in with it now.');
      navigate('/login');
    } catch (error) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err.response?.data?.detail || 'Could not reset the password. The link may have expired.');
    } finally {
      setIsSubmitting(false);
    }
  };

  let submitLabel = 'Set the new password';
  if (isSubmitting) submitLabel = 'Saving';

  return (
    <AuthShell
      title={<>Choose a new password.<br /><span className="text-[#92949d]">Then sign in with it.</span></>}
      sub="No link needed after this; the login page works with your email."
    >
      {!token && (
        <div className="mb-4 rounded-md border border-[#e8b4b4] bg-[#fdf3f3] px-3 py-2 text-[13px] text-[#8a2b2b]">
          This page was opened without a reset token. Ask for a new link from Forgot your password.
        </div>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <PasswordField
          id="reset-password"
          label="New password"
          value={password}
          onChange={setPassword}
          placeholder="New password"
          autoComplete="new-password"
          autoFocus
        />
        <PasswordField
          id="reset-password-again"
          label="Type it again"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Type it again"
          autoComplete="new-password"
        />
        <span className={`text-[12.5px] ${hintCls}`}>{hint}</span>
        <button type="submit" disabled={isSubmitting || !long || !same || !confirmPassword} className={PRIMARY}>{submitLabel}</button>
      </form>
      <p className={FOOTNOTE}>Remembered it? <Link to="/login" className={FOOTLINK}>Sign in</Link>.</p>
    </AuthShell>
  );
};

export default ResetPassword;
